// Démonstration locale du studio : mêmes versions et gestes, sans fournisseur ni publication.
import { creationStudioSchema, type TexteStudio, type VersionTexteStudio } from '@artosera/shared';
import { ApiError } from './errors.js';

const textes = new Map<string, TexteStudio>();

const date = () => new Date().toISOString();

let compteur = 1;

function id() {
  return (compteur++).toString(16).padStart(24, '0');
}

function version(
  n: number,
  origine: VersionTexteStudio['origine'],
  contenu?: VersionTexteStudio['contenu'],
): VersionTexteStudio {
  return {
    n,
    contenu: contenu ?? {
      titre: 'À la galerie',
      chapo: 'Une proposition à relire.',
      corps:
        'La galerie vous invite à découvrir les œuvres présentées.\n\nCe texte de démonstration reste une proposition.',
    },
    aVerifier: ['Confirmer les informations pratiques.'],
    origine,
    consigne: null,
    appelId: origine === 'humain' ? null : 'demo-studio',
    creePar: 'compte-demo',
    creeLe: date(),
  };
}

// Textes fictifs : une validation humaine et un brouillon à reprendre dans le studio.
const communique: TexteStudio = {
  id: id(),
  type: 'communique',
  titreInterne: 'Communiqué — Les formes du silence',
  source: { type: 'libre', ids: [] },
  brief: 'Présenter une exposition fictive autour des matières et des espaces de silence.',
  langue: 'fr',
  longueur: 'moyenne',
  mentionnerPrixPublics: false,
  etat: 'valide',
  versions: [
    {
      ...version(1, 'ia', {
        titre: 'Les formes du silence',
        chapo: 'Une exposition de démonstration consacrée aux matières et à la lumière.',
        corps:
          'La galerie réunit peintures et sculptures dans un parcours autour du silence. Les surfaces mates et les volumes ouverts invitent à prendre le temps de regarder.\n\nCette exposition est fictive et sert uniquement à découvrir le studio.',
      }),
      creeLe: '2026-10-12T09:00:00.000Z',
    },
    {
      ...version(2, 'humain', {
        titre: 'Les formes du silence',
        chapo: 'Un parcours fictif entre surfaces, volumes et lumière.',
        corps:
          'Les formes du silence fait dialoguer peintures et sculptures. Les textures absorbent la lumière, les volumes ménagent des ouvertures : chaque œuvre propose une autre manière de percevoir l’espace.\n\nLe parcours invite à ralentir et à observer les relations entre les matières. Cette exposition de démonstration est entièrement fictive.',
      }),
      aVerifier: [],
      creeLe: '2026-10-13T14:30:00.000Z',
    },
  ],
  versionValidee: 2,
  traductionDe: null,
  traductions: {},
  utilisations: [],
  creePar: 'compte-demo',
  creeLe: '2026-10-12T09:00:00.000Z',
  modifieLe: '2026-10-13T14:30:00.000Z',
  version: 3,
};
const reseaux: TexteStudio = {
  ...structuredClone(communique),
  id: id(),
  type: 'publication_reseaux',
  titreInterne: 'Réseaux — Invitation à regarder autrement',
  brief: 'Proposer trois invitations courtes pour les réseaux, sans mot-dièse ni date.',
  longueur: 'courte',
  etat: 'brouillon',
  versions: [
    {
      ...version(1, 'ia', {
        titre: '',
        chapo: '',
        corps:
          'Une surface, une ombre, un volume : prenez le temps de regarder autrement. Découvrez notre exposition fictive Les formes du silence.\n\nEt si le silence prenait forme ? Peintures et sculptures dialoguent dans ce parcours de démonstration.\n\nLaissez votre regard suivre les matières et la lumière. Les formes du silence vous invite à ralentir, le temps d’une exposition fictive.',
      }),
      aVerifier: ['Choisir une variante et adapter le ton avant toute utilisation.'],
      creeLe: '2026-10-14T10:00:00.000Z',
    },
  ],
  versionValidee: null,
  creeLe: '2026-10-14T10:00:00.000Z',
  modifieLe: '2026-10-14T10:00:00.000Z',
  version: 1,
};
textes.set(communique.id, communique);
textes.set(reseaux.id, reseaux);

export function reponseDemoStudio(
  chemin: string,
  params: URLSearchParams,
  methode: string,
  corps?: unknown,
): unknown {
  if (chemin === '/studio/sources') return { sources: [] };
  if (chemin === '/studio' && methode === 'GET') {
    const liste = [...textes.values()].filter(
      (t) =>
        (!params.get('q') ||
          t.titreInterne.toLowerCase().includes(params.get('q')!.toLowerCase())) &&
        (!params.get('type') || t.type === params.get('type')) &&
        (!params.get('etat') || t.etat === params.get('etat')) &&
        (!params.get('langue') || t.langue === params.get('langue')) &&
        (!params.get('source') || t.source.type === params.get('source')),
    );
    const page = Number(params.get('page') ?? 1);
    return structuredClone({
      textes: liste.slice((page - 1) * 50, page * 50),
      page,
      total: liste.length,
    });
  }
  if (chemin === '/studio' && methode === 'POST') {
    const c = creationStudioSchema.parse(corps),
      identifiant = id(),
      v = version(1, 'ia');
    if (c.type === 'texte_salle' || c.type === 'libre') v.contenu.chapo = '';
    if (c.type === 'publication_reseaux')
      v.contenu = {
        titre: '',
        chapo: '',
        corps: 'Première proposition.\n\nDeuxième proposition.\n\nTroisième proposition.',
      };
    const t: TexteStudio = {
      ...c,
      id: identifiant,
      etat: 'brouillon',
      versions: [v],
      versionValidee: null,
      traductionDe: null,
      traductions: {},
      utilisations: [],
      creePar: 'compte-demo',
      creeLe: date(),
      modifieLe: date(),
      version: 1,
    };
    textes.set(t.id, t);
    return structuredClone({ texte: t });
  }
  const morceaux = /^\/studio\/([^/]+)(?:\/([^/]+))?$/.exec(chemin);
  const t = textes.get(morceaux?.[1] ?? '');
  if (!t) throw new ApiError(404, 'ressource.introuvable', 'Texte introuvable.');
  if (methode === 'GET') return structuredClone({ texte: t });
  const c = corps as {
    version: number;
    n?: number;
    contenu?: VersionTexteStudio['contenu'];
    consigne?: string;
    aVerifier?: string[];
    type?: 'copie' | 'telechargement' | 'actualite';
  };
  if (c.version !== t.version) throw new ApiError(409, 'conflit.version', 'Ce texte a changé.');
  const geste = morceaux?.[2],
    v = t.versions.find((v) => v.n === c.n) ?? t.versions.at(-1)!;
  if (geste === 'modifier' || geste === 'retoucher') {
    const nouvelle = version(
      t.versions.at(-1)!.n + 1,
      geste === 'modifier' ? 'humain' : 'ia',
      c.contenu ?? v.contenu,
    );
    nouvelle.consigne = c.consigne ?? null;
    nouvelle.aVerifier = c.aVerifier ?? v.aVerifier;
    t.versions.push(nouvelle);
    t.etat = 'brouillon';
    if (t.versions.length > 20)
      t.versions.splice(
        t.versions.findIndex((v) => v.n !== t.versionValidee),
        1,
      );
  }
  if (geste === 'valider') {
    t.versionValidee = v.n;
    t.etat = 'valide';
  }
  if (geste === 'archiver') t.etat = 'archive';
  if (geste === 'traduire') {
    const langue = t.langue === 'fr' ? 'en' : 'fr',
      identifiant = id();
    const existante = textes.get(t.traductions[langue] ?? '');
    if (existante) {
      existante.versions.push(version(existante.versions.at(-1)!.n + 1, 'traduction', v.contenu));
      if (existante.versions.length > 20)
        existante.versions.splice(
          existante.versions.findIndex((v) => v.n !== existante.versionValidee),
          1,
        );
      existante.etat = 'brouillon';
      existante.version++;
      existante.modifieLe = date();
      t.version++;
      return structuredClone({ texte: existante });
    }
    const traduction: TexteStudio = {
      ...structuredClone(t),
      id: identifiant,
      langue,
      version: 1,
      etat: 'brouillon',
      versionValidee: null,
      versions: [version(1, 'traduction', v.contenu)],
      traductionDe: t.id,
      traductions: { [t.langue]: t.id },
      utilisations: [],
    };
    t.traductions[langue] = identifiant;
    t.version++;
    textes.set(identifiant, traduction);
    return structuredClone({ texte: traduction });
  }
  if (geste === 'utiliser') {
    if (t.etat !== 'valide') throw new ApiError(409, 'conflit.version', 'Validez le texte.');
    t.utilisations.push({
      type: c.type!,
      par: 'compte-demo',
      le: date(),
      ...(c.type === 'actualite' ? { postId: 'actualite-demo' } : {}),
    });
  }
  t.version++;
  t.modifieLe = date();
  const validee = t.versions.find((v) => v.n === t.versionValidee);
  return structuredClone({
    texte: t,
    ...(geste === 'utiliser' && validee
      ? {
          contenu: [validee.contenu.titre, validee.contenu.chapo, validee.contenu.corps]
            .filter(Boolean)
            .join('\n\n'),
          ...(c.type === 'actualite' ? { postId: 'actualite-demo' } : {}),
        }
      : {}),
  });
}
